/**
 * Service de monétisation pour l'application Dis Maman!
 * Gère les abonnements, limites et permissions utilisateur
 */

// Plans d'abonnement disponibles
export const SUBSCRIPTION_PLANS = [
  {
    id: 'admin',
    name: 'Admin (mensuel)',
    price: 0,
    currency: 'EUR',
    duration: 'unlimited',
    stripeProductId: null,
    stripePriceId: null,
    features: [
      'Questions illimitées',
      'Profils enfants illimités',
      'Historique complet',
      'Histoires illimitées',
      'Accès admin'
    ],
    limits: {
      questionsPerDay: -1, // illimité
      childProfiles: -1, // illimité
      historyDays: -1, // illimité
      storiesPerMonth: -1, // illimité
      quizAccess: true,
    }
  },
  {
    id: 'free',
    name: 'Gratuit',
    price: 0,
    currency: 'EUR',
    duration: 'unlimited',
    stripeProductId: null,
    stripePriceId: null,
    features: [
      'Un « pourquoi » par jour',
      '1 profil enfant',
      '1 histoire par mois',
      'Pas d\'accès aux révisions (Quiz)'
    ],
    limits: {
      questionsPerDay: 1,
      childProfiles: 1,
      historyDays: 7,
      storiesPerMonth: 1,
      quizAccess: false,
    }
  },
  {
    id: 'essential_monthly',
    name: 'Explorateur',
    price: 4.99,
    currency: 'EUR',
    duration: 'monthly',
    stripeProductId: 'prod_T5UPrkuyHk0XLv',
    stripePriceId: 'price_1S9JQRQPJb5o0TOC94BSXqq9',
    features: [
      'Ne manquez jamais un « pourquoi » de votre enfant',
      '1 profil enfant',
      'Toutes ses questions gardées en mémoire',
      'Des réponses qui grandissent avec lui',
      'Une nouvelle aventure chaque semaine'
    ],
    limits: {
      questionsPerDay: 'unlimited',
      childProfiles: 1,
      historyDays: 'unlimited',
      storiesPerMonth: 5,
    }
  },
  {
    id: 'essential_yearly',
    name: 'Explorateur Annuel',
    price: 49.90,
    currency: 'EUR',
    duration: 'yearly',
    stripeProductId: 'prod_T5UPrkuyHk0XLv',
    stripePriceId: 'price_1S9JZHQPJb5o0TOCkvZp3V3G',
    originalPrice: 59.88, // 4.99 x 12
    monthsOffered: 2, // 2 mois offerts
    savingsPercent: 17,
    features: [
      'Ne manquez jamais un « pourquoi » de votre enfant',
      '1 profil enfant',
      'Toutes ses questions gardées en mémoire',
      'Des réponses qui grandissent avec lui',
      'Une nouvelle aventure chaque semaine',
      '2 mois offerts'
    ],
    limits: {
      questionsPerDay: 'unlimited',
      childProfiles: 1,
      historyDays: 'unlimited',
      storiesPerMonth: 5,
    }
  },
  {
    id: 'family_monthly',
    name: 'Famille',
    price: 9.99,
    currency: 'EUR',
    duration: 'monthly',
    stripeProductId: 'prod_T5UUANLsIiIGWU',
    stripePriceId: 'price_1S9JYPQPJb5o0TOCO72BCC3M',
    features: [
      'Tous les « pourquoi » de vos enfants, sans limite',
      'Jusqu\'à 4 profils enfants',
      'Chaque enfant a son univers personnalisé',
      'Des réponses qui grandissent avec eux',
      'Deux nouvelles aventures par semaine'
    ],
    limits: {
      questionsPerDay: 'unlimited',
      childProfiles: 4,
      historyDays: 'unlimited',
      storiesPerMonth: 10,
    }
  },
  {
    id: 'family_yearly',
    name: 'Famille Annuel',
    price: 99.90,
    currency: 'EUR',
    duration: 'yearly',
    stripeProductId: 'prod_T5UUANLsIiIGWU',
    stripePriceId: 'price_1S9JVzQPJb5o0TOCmrBC6MFi',
    originalPrice: 119.88, // 9.99 x 12
    monthsOffered: 2, // 2 mois offerts
    savingsPercent: 17,
    features: [
      'Tous les « pourquoi » de vos enfants, sans limite',
      'Jusqu\'à 4 profils enfants',
      'Chaque enfant a son univers personnalisé',
      'Des réponses qui grandissent avec eux',
      'Deux nouvelles aventures par semaine',
      '2 mois offerts'
    ],
    limits: {
      questionsPerDay: 'unlimited',
      childProfiles: 4,
      historyDays: 'unlimited',
      storiesPerMonth: 10,
    }
  }
];

class MonetizationService {
  constructor() {
    this.userSubscription = null;
    this.usageStats = null;
    this.backendURL = process.env.REACT_APP_BACKEND_URL;
    this.isInitialized = false;
  }

  static getInstance() {
    if (!MonetizationService.instance) {
      MonetizationService.instance = new MonetizationService();
    }
    return MonetizationService.instance;
  }

  /**
   * Initialise le service avec les données de l'utilisateur
   */
  async initialize() {
    if (this.isInitialized) return;
    
    try {
      await Promise.all([
        this.refreshSubscriptionStatus(),
        this.refreshUsageStats()
      ]);
      this.isInitialized = true;
      console.log('✅ MonetizationService initialisé');
    } catch (error) {
      console.error('❌ Erreur initialisation MonetizationService:', error);
    }
  }

  /**
   * Vérifie si l'utilisateur peut effectuer une action
   */
  async canPerformAction(action) {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return { allowed: false, reason: 'Non authentifié', upgradeRequired: true };
      }

      const response = await fetch(`${this.backendURL}/api/monetization/check-permission?action=${action}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        return { allowed: false, reason: 'Erreur de vérification', upgradeRequired: true };
      }

      return await response.json();

    } catch (error) {
      console.error('Erreur vérification permission:', error);
      
      // Fallback vers vérification locale si disponible
      if (this.userSubscription && this.usageStats) {
        return this.canPerformActionLocally(action);
      }
      
      return { allowed: false, reason: 'Erreur de connexion', upgradeRequired: true };
    }
  }

  /**
   * Vérification locale des permissions (fallback) - DÉSACTIVÉE
   * Ne plus utiliser de vérification locale, toujours passer par le backend
   */
  canPerformActionLocally(action) {
    console.warn('⚠️ Vérification locale désactivée - utilisation backend obligatoire');
    
    // Toujours autoriser en cas de fallback pour éviter les blocages
    // Le backend fait la vraie vérification
    return { allowed: true, reason: 'Vérification backend requise' };
  }

  /**
   * Récupère le statut d'abonnement depuis le backend
   */
  async refreshSubscriptionStatus() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${this.backendURL}/api/monetization/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur récupération statut abonnement');
      }
      
      const data = await response.json();
      
      // Conversion des dates
      if (data.startDate) data.startDate = new Date(data.startDate);
      if (data.endDate) data.endDate = new Date(data.endDate);
      if (data.nextBillingDate) data.nextBillingDate = new Date(data.nextBillingDate);
      
      this.userSubscription = data;
      await this.saveSubscriptionToCache(data);
      
      return data;
    } catch (error) {
      console.error('Erreur refresh subscription:', error);
      
      // Fallback vers le cache local
      const cached = await this.loadSubscriptionFromCache();
      if (cached) {
        this.userSubscription = cached;
        return cached;
      }
      
      throw error;
    }
  }

  /**
   * Récupère les statistiques d'utilisation
   */
  async refreshUsageStats() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${this.backendURL}/api/monetization/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur récupération statistiques');
      }
      
      const data = await response.json();
      this.usageStats = data;
      await this.saveUsageStatsToCache(data);
      
      return data;
    } catch (error) {
      console.error('Erreur refresh usage stats:', error);
      
      // Fallback vers le cache local
      const cached = await this.loadUsageStatsFromCache();
      if (cached) {
        this.usageStats = cached;
        return cached;
      }
      
      // Valeurs par défaut en cas d'erreur
      this.usageStats = {
        questionsToday: 0,
        questionsThisMonth: 0,
        childrenCount: 0,
        favoriteMessages: 0,
        historyDays: 0,
        exportCount: 0,
      };
      
      return this.usageStats;
    }
  }

  /**
   * Démarre un abonnement via Stripe
   */
  async subscribeToPlan(planId) {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${this.backendURL}/api/monetization/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ planId })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, error: data.detail || 'Erreur lors de la création de l\'abonnement' };
      }

      // Si Stripe retourne un client_secret, c'est pour confirmer le paiement
      if (data.clientSecret) {
        return { success: true, clientSecret: data.clientSecret };
      }

      // Sinon, abonnement créé avec succès
      if (data.success) {
        await this.refreshSubscriptionStatus();
        return { success: true };
      }

      return { success: false, error: 'Erreur inconnue' };
      
    } catch (error) {
      console.error('Erreur subscribe to plan:', error);
      return { success: false, error: 'Erreur de connexion' };
    }
  }

  /**
   * Annule un abonnement
   */
  async cancelSubscription() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${this.backendURL}/api/monetization/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, error: data.detail || 'Erreur annulation' };
      }

      if (data.success) {
        await this.refreshSubscriptionStatus();
        return { success: true };
      }
      
      return { success: false, error: 'Erreur inconnue' };
      
    } catch (error) {
      console.error('Erreur cancel subscription:', error);
      return { success: false, error: 'Erreur de connexion' };
    }
  }

  /**
   * Met à jour le mode de paiement
   */
  async updatePaymentMethod() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${this.backendURL}/api/monetization/update-payment-method`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, error: data.detail || 'Erreur mise à jour paiement' };
      }

      return { success: true, clientSecret: data.clientSecret };
      
    } catch (error) {
      console.error('Erreur update payment method:', error);
      return { success: false, error: 'Erreur de connexion' };
    }
  }

  // Getters pour l'état actuel
  getCurrentSubscription() {
    return this.userSubscription;
  }

  getCurrentUsage() {
    return this.usageStats;
  }

  getCurrentPlan() {
    if (!this.userSubscription) return null;
    return SUBSCRIPTION_PLANS.find(p => p.id === this.userSubscription.planId) || null;
  }

  // Utilitaires
  formatPrice(plan) {
    if (plan.price === 0) return 'Gratuit';
    
    const price = plan.price.toFixed(2);
    const duration = plan.duration === 'monthly' ? '/mois' : '/an';
    
    if (plan.originalPrice && plan.savings) {
      return `${price}€${duration} (au lieu de ${plan.originalPrice.toFixed(2)}€)`;
    }
    
    return `${price}€${duration}`;
  }

  getUpgradeMessage(action) {
    const messages = {
      ask_question: 'Passez à Explorateur pour répondre à toutes ses questions !',
      add_child: 'Passez à Explorateur pour ajouter plus de profils d\'enfants !',
      export_data: 'L\'export de données est réservé aux abonnés Explorateur !',
      advanced_stats: 'Les statistiques avancées sont réservées aux abonnés Explorateur !',
      modify_child: 'Cette limitation de 48h empêche les abus d\'abonnement.',
    };
    return messages[action] || 'Passez à Explorateur pour débloquer cette fonctionnalité !';
  }

  /**
   * Récupère le statut de modification d'enfant (limite 48h)
   */
  async getChildModificationStatus() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${this.backendURL}/api/children/modification-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur récupération statut modification');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur child modification status:', error);
      return {
        can_modify: true, // Par défaut on autorise
        reason: null,
        hours_remaining: 0,
        last_modification: null
      };
    }
  }

  // États booléens
  get isPremium() {
    const planId = this.getCurrentPlan()?.id;
    return this.userSubscription?.status === 'active' && planId !== 'free' && planId !== 'free_trial' && planId != null;
  }

  get isFamily() {
    const plan = this.getCurrentPlan();
    return plan?.id === 'family_monthly' || plan?.id === 'family_yearly';
  }

  get isEssential() {
    const plan = this.getCurrentPlan();
    return plan?.id === 'essential_monthly' || plan?.id === 'essential_yearly';
  }

  get isYearly() {
    return this.getCurrentPlan()?.duration === 'yearly';
  }

  /**
   * Vide complètement le cache de monétisation
   */
  clearCache() {
    try {
      localStorage.removeItem('@subscription_cache');
      localStorage.removeItem('@usage_stats_cache');
      this.userSubscription = null;
      this.usageStats = null;
      this.isInitialized = false;
      console.log('✅ Cache de monétisation vidé');
    } catch (error) {
      console.error('❌ Erreur vidage cache:', error);
    }
  }

  /**
   * Force la récupération de données fraîches depuis le backend
   */
  async forceRefresh() {
    this.clearCache();
    await this.initialize();
    return {
      subscription: this.userSubscription,
      usage: this.usageStats
    };
  }

  // Méthodes de cache privées
  async saveSubscriptionToCache(subscription) {
    try {
      localStorage.setItem('@subscription_cache', JSON.stringify(subscription));
    } catch (error) {
      console.error('Erreur sauvegarde cache subscription:', error);
    }
  }

  async loadSubscriptionFromCache() {
    try {
      const cached = localStorage.getItem('@subscription_cache');
      if (cached) {
        const data = JSON.parse(cached);
        // Reconvertir les dates
        if (data.startDate) data.startDate = new Date(data.startDate);
        if (data.endDate) data.endDate = new Date(data.endDate);
        if (data.nextBillingDate) data.nextBillingDate = new Date(data.nextBillingDate);
        return data;
      }
    } catch (error) {
      console.error('Erreur chargement cache subscription:', error);
    }
    return null;
  }

  async saveUsageStatsToCache(stats) {
    try {
      localStorage.setItem('@usage_stats_cache', JSON.stringify(stats));
    } catch (error) {
      console.error('Erreur sauvegarde cache usage:', error);
    }
  }

  async loadUsageStatsFromCache() {
    try {
      const cached = localStorage.getItem('@usage_stats_cache');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Erreur chargement cache usage:', error);
    }
    return null;
  }
}

export default MonetizationService;